import { useMutation } from "@apollo/client";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useState } from "react";

import {
  ADMIN_SELECTION_USER_IDS,
  BULK_INVITE_WITH_USER_IDS,
  VALIDATE_FOR_SELECTION_BULK_INVITE,
} from "../../gql/mutations/event";
import { RECORD_INVITES } from "../../gql/mutations/outreach";
import { SHARE_EVENT_TO_USER_IDS } from "../../gql/queries/shareEvent";

/**
 * Invite / Share one event to everyone on a given clock (a UTC offset), straight
 * from the timezone-supply drill. Mirrors the calendar's CalendarZoneOutreach,
 * but targets a raw offset (in minutes) rather than a picked timezone.
 *
 * Invite reserves a seat (a full event is refused, shown inline); Share reserves
 * nothing (capacity-free) so it always resolves its audience. Nothing sends
 * until you confirm.
 */

type Action = "invite" | "share";

interface Preview {
  eventTitle: string;
  userIds: string[];
  warnings: string[];
}

interface Props {
  eventId: string;
  eventTitle: string;
  /** UTC offset of the region, in minutes (e.g. +60 for UTC+1). */
  offsetMinutes: number;
  /** Region label, for the confirmation copy. */
  regionLabel: string;
}

const RegionOutreach: React.FC<Props> = ({ eventId, eventTitle, offsetMinutes, regionLabel }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [action, setAction] = useState<Action | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [validate, { loading: validating }] = useMutation(VALIDATE_FOR_SELECTION_BULK_INVITE);
  const [selectIds, { loading: selecting }] = useMutation(ADMIN_SELECTION_USER_IDS);
  const [invite, { loading: inviting }] = useMutation(BULK_INVITE_WITH_USER_IDS);
  const [recordCohort] = useMutation(RECORD_INVITES);
  const [share, { loading: sharing }] = useMutation(SHARE_EVENT_TO_USER_IDS);

  const filters = { timezoneOffsetMinutes: offsetMinutes, all: true };
  const busy = validating || selecting || inviting || sharing;

  const startPreview = async (next: Action): Promise<void> => {
    setAction(next);
    setPreview(null);
    setError(null);
    try {
      if (next === "share") {
        const { data } = await selectIds({ variables: { filters } });
        setPreview({ eventTitle, userIds: data?.adminSelectionUserIds ?? [], warnings: [] });
      } else {
        const { data } = await validate({ variables: { eventId, filters } });
        const result = data?.validateForSelectionBulkInvite;
        setPreview({
          eventTitle: result?.eventTitle ?? eventTitle,
          userIds: result?.userIds ?? [],
          warnings: result?.warningMessages ?? [],
        });
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Something went wrong.");
    }
  };

  const cancel = (): void => {
    setAction(null);
    setPreview(null);
    setError(null);
  };

  const send = async (): Promise<void> => {
    if (!preview || preview.userIds.length === 0) {
      return;
    }
    try {
      if (action === "invite") {
        await invite({ variables: { eventId, input: { userIds: preview.userIds } } });
        try {
          await recordCohort({
            variables: {
              eventId,
              userIds: preview.userIds,
              source: "ZONE_OUTREACH",
              filters: [`Region: ${regionLabel}`],
            },
          });
        } catch {
          // The invite succeeded regardless — the log is a record, not a gate.
        }
        enqueueSnackbar(`Invited ${preview.userIds.length} member(s) in ${regionLabel}.`, {
          variant: "success",
        });
      } else {
        const { data } = await share({
          variables: { eventId, userIds: preview.userIds, source: "ZONE_OUTREACH" },
        });
        const sharedCount = data?.adminShareEventToUserIds?.sharedCount ?? preview.userIds.length;
        enqueueSnackbar(`Shared with ${sharedCount} member(s) in ${regionLabel}.`, {
          variant: "success",
        });
      }
      cancel();
    } catch (caught) {
      enqueueSnackbar(caught instanceof Error ? caught.message : "Send failed.", {
        variant: "error",
      });
    }
  };

  if (!action) {
    return (
      <Stack direction="row" spacing={0.5}>
        <Button size="small" variant="outlined" onClick={() => startPreview("invite")}>
          Invite
        </Button>
        <Button size="small" variant="outlined" onClick={() => startPreview("share")}>
          Share
        </Button>
      </Stack>
    );
  }

  const ready = !error && preview && preview.userIds.length > 0;
  const isCapacity = Boolean(error && /invite only|max number of participants/i.test(error));

  return (
    <Alert
      severity={error ? "warning" : preview && preview.userIds.length === 0 ? "warning" : "info"}
      sx={{ mt: 0.5 }}
      action={
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" color="inherit" onClick={cancel} disabled={busy}>
            Cancel
          </Button>
          {!error && (
            <Button size="small" variant="contained" onClick={send} disabled={busy || !ready}>
              {busy
                ? "Working…"
                : `${action === "invite" ? "Invite" : "Share"} ${preview?.userIds.length ?? ""}`}
            </Button>
          )}
          {isCapacity && (
            <Button size="small" variant="contained" onClick={() => startPreview("share")}>
              Share instead
            </Button>
          )}
        </Stack>
      }
    >
      {error ? (
        <Typography variant="body2">
          {error}
          {isCapacity && (
            <Box component="span" sx={{ color: "text.secondary" }}>
              {" "}
              An invite reserves a seat. <strong>Share</strong> instead to reach everyone in{" "}
              {regionLabel}.
            </Box>
          )}
        </Typography>
      ) : busy || !preview ? (
        <Typography variant="body2">Checking who’s in {regionLabel}…</Typography>
      ) : (
        <Typography variant="body2">
          <strong>{preview.userIds.length}</strong> member{preview.userIds.length === 1 ? "" : "s"}{" "}
          in <strong>{regionLabel}</strong> would be {action === "invite" ? "invited to" : "sent"}{" "}
          <strong>{preview.eventTitle}</strong>.
        </Typography>
      )}
    </Alert>
  );
};

export default RegionOutreach;
